import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Users, Clock, Home, Layers, TrendingUp, Eye, MousePointer, DollarSign } from 'lucide-react';
const engagementData = [{
  name: 'Mon',
  sessions: 40,
  time: 240,
  buyers: 12
}, {
  name: 'Tue',
  sessions: 30,
  time: 139,
  buyers: 8
}, {
  name: 'Wed',
  sessions: 20,
  time: 980,
  buyers: 15
}, {
  name: 'Thu',
  sessions: 27,
  time: 390,
  buyers: 10
}, {
  name: 'Fri',
  sessions: 18,
  time: 480,
  buyers: 14
}, {
  name: 'Sat',
  sessions: 23,
  time: 380,
  buyers: 9
}, {
  name: 'Sun',
  sessions: 34,
  time: 430,
  buyers: 11
}];
const modelTimeData = [{
  name: 'The Lawson',
  time: 2840,
  buyers: 45,
  avgTime: 63,
  upgradeValue: 142500
}, {
  name: 'The Samson',
  time: 1920,
  buyers: 32,
  avgTime: 60,
  upgradeValue: 128800
}, {
  name: 'The Pine',
  time: 1680,
  buyers: 28,
  avgTime: 60,
  upgradeValue: 0
}, {
  name: 'The Cedar',
  time: 1140,
  buyers: 19,
  avgTime: 60,
  upgradeValue: 0
}, {
  name: 'The Birch',
  time: 720,
  buyers: 12,
  avgTime: 60,
  upgradeValue: 0
}];
const optionsData = [{
  name: 'Quartz Countertops',
  count: 85,
  percentage: 68,
  revenue: 42500
}, {
  name: 'Hardwood Flooring',
  count: 72,
  percentage: 58,
  revenue: 51840
}, {
  name: 'Smart Home Package',
  count: 65,
  percentage: 52,
  revenue: 58500
}, {
  name: 'Finished Basement',
  count: 45,
  percentage: 36,
  revenue: 202500
}, {
  name: 'Deck Extension',
  count: 38,
  percentage: 30,
  revenue: 23560
}];
const elevationData = [{
  name: 'Modern',
  value: 42,
  color: '#06b6d4'
}, {
  name: 'Traditional',
  value: 28,
  color: '#334155'
}, {
  name: 'Craftsman',
  value: 18,
  color: '#64748b'
}, {
  name: 'Contemporary',
  value: 12,
  color: '#94a3b8'
}];
const floorplanData = [{
  name: 'Open Concept',
  value: 65
}, {
  name: 'Traditional',
  value: 25
}, {
  name: 'Split Level',
  value: 10
}];
const colorPaletteData = [{
  name: 'Modern Neutral',
  count: 48,
  color: '#e2e8f0'
}, {
  name: 'Warm Earth',
  count: 32,
  color: '#d4a574'
}, {
  name: 'Cool Gray',
  count: 28,
  color: '#94a3b8'
}, {
  name: 'Classic White',
  count: 16,
  color: '#f8fafc'
}];
const upgradeRevenueData = [{
  name: 'Week 1',
  revenue: 45200
}, {
  name: 'Week 2',
  revenue: 52800
}, {
  name: 'Week 3',
  revenue: 48500
}, {
  name: 'Week 4',
  revenue: 61300
}, {
  name: 'Week 5',
  revenue: 58400
}, {
  name: 'Week 6',
  revenue: 72100
}, {
  name: 'Week 7',
  revenue: 68500
}];
const projects = ['All Projects', 'The Lawson', 'The Samson', 'The Pine', 'The Cedar', 'The Birch'];
export function AnalyticsPage() {
  const [selectedProject, setSelectedProject] = useState('All Projects');
  const [timeRange, setTimeRange] = useState('7d');
  // Upgrade stats
  const upgradeStats = {
    totalRevenue: 271300,
    avgPerCustomer: 45217,
    topCategory: 'Kitchen',
    conversionRate: 68
  };
  return <div className="space-y-8 max-w-7xl mx-auto" data-id="element-3349">
      {/* Header with Filters */}
      <div className="flex items-center justify-between" data-id="element-3350">
        <div data-id="element-3351">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-3352">Analytics</h1>
          <p className="text-slate-500" data-id="element-3353">
            Deep insights into buyer behavior and upgrade revenue
          </p>
        </div>
        <div className="flex gap-3" data-id="element-3354">
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-3355">
            {projects.map(project => <option key={project} value={project} data-id="element-3356">
                {project}
              </option>)}
          </select>
          <select value={timeRange} onChange={e => setTimeRange(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-3357">
            <option value="7d" data-id="element-3358">Last 7 Days</option>
            <option value="30d" data-id="element-3359">Last 30 Days</option>
            <option value="90d" data-id="element-3360">Last 90 Days</option>
          </select>
          <Button variant="outline" data-id="element-3361">Export Report</Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3362">
        <Card data-id="element-3363">
          <CardContent className="p-6" data-id="element-3364">
            <div className="flex items-center gap-4" data-id="element-3365">
              <div className="p-3 bg-emerald-50 rounded-lg flex-shrink-0" data-id="element-3366">
                <DollarSign className="h-6 w-6 text-emerald-600" data-id="element-3367" />
              </div>
              <div data-id="element-3368">
                <p className="text-sm font-medium text-slate-500" data-id="element-3369">
                  Total Upgrade Revenue
                </p>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-3370">
                  ${upgradeStats.totalRevenue.toLocaleString()}
                </h3>
                <p className="text-xs text-green-600 flex items-center mt-1" data-id="element-3371">
                  <TrendingUp className="h-3 w-3 mr-1" data-id="element-3372" />
                  <span className="font-medium" data-id="element-3373">+18%</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-3374">
          <CardContent className="p-6" data-id="element-3375">
            <div className="flex items-center gap-4" data-id="element-3376">
              <div className="p-3 bg-purple-50 rounded-lg flex-shrink-0" data-id="element-3377">
                <TrendingUp className="h-6 w-6 text-purple-600" data-id="element-3378" />
              </div>
              <div data-id="element-3379">
                <p className="text-sm font-medium text-slate-500" data-id="element-3380">
                  Avg Upgrade Value
                </p>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-3381">
                  ${upgradeStats.avgPerCustomer.toLocaleString()}
                </h3>
                <p className="text-xs text-slate-500 mt-1" data-id="element-3382">Per customer</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-3383">
          <CardContent className="p-6" data-id="element-3384">
            <div className="flex items-center gap-4" data-id="element-3385">
              <div className="p-3 bg-primary-50 rounded-lg flex-shrink-0" data-id="element-3386">
                <Users className="h-6 w-6 text-primary-600" data-id="element-3387" />
              </div>
              <div data-id="element-3388">
                <p className="text-sm font-medium text-slate-500" data-id="element-3389">
                  Total Buyers
                </p>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-3390">124</h3>
                <p className="text-xs text-green-600 flex items-center mt-1" data-id="element-3391">
                  <TrendingUp className="h-3 w-3 mr-1" data-id="element-3392" />
                  <span className="font-medium" data-id="element-3393">+12%</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-3394">
          <CardContent className="p-6" data-id="element-3395">
            <div className="flex items-center gap-4" data-id="element-3396">
              <div className="p-3 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-3397">
                <MousePointer className="h-6 w-6 text-blue-600" data-id="element-3398" />
              </div>
              <div data-id="element-3399">
                <p className="text-sm font-medium text-slate-500" data-id="element-3400">
                  Selection Rate
                </p>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-3401">
                  {upgradeStats.conversionRate}%
                </h3>
                <p className="text-xs text-slate-500 mt-1" data-id="element-3402">
                  Buyers with upgrades
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upgrade Revenue Chart */}
      <Card data-id="element-3403">
        <CardHeader data-id="element-3404">
          <CardTitle data-id="element-3405">Upgrade Revenue Over Time</CardTitle>
        </CardHeader>
        <CardContent data-id="element-3406">
          <div className="h-[300px] w-full" data-id="element-3407">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3408">
              <AreaChart data={upgradeRevenueData} data-id="element-3409">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-3410" />
                <XAxis dataKey="name" tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-3411" />
                <YAxis tick={{
                fill: '#64748b',
                fontSize: 12
              }} tickFormatter={value => `$${(value / 1000).toFixed(0)}K`} data-id="element-3412" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']} data-id="element-3413" />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="#d1fae5" strokeWidth={2} data-id="element-3414" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3415">
        <Card data-id="element-3416">
          <CardHeader data-id="element-3417">
            <CardTitle data-id="element-3418">Engagement Over Time</CardTitle>
          </CardHeader>
          <CardContent data-id="element-3419">
            <div className="h-[300px] w-full" data-id="element-3420">
              <ResponsiveContainer width="100%" height="100%" data-id="element-3421">
                <LineChart data={engagementData} data-id="element-3422">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-3423" />
                  <XAxis dataKey="name" tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} data-id="element-3424" />
                  <YAxis tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} data-id="element-3425" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-3426" />
                  <Legend data-id="element-3427" />
                  <Line type="monotone" dataKey="sessions" stroke="#06b6d4" strokeWidth={2} name="Sessions" data-id="element-3428" />
                  <Line type="monotone" dataKey="buyers" stroke="#334155" strokeWidth={2} name="New Buyers" data-id="element-3429" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-3430">
          <CardHeader data-id="element-3431">
            <CardTitle data-id="element-3432">Model Engagement</CardTitle>
          </CardHeader>
          <CardContent data-id="element-3433">
            <div className="h-[300px] w-full" data-id="element-3434">
              <ResponsiveContainer width="100%" height="100%" data-id="element-3435">
                <BarChart data={modelTimeData} layout="vertical" data-id="element-3436">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" data-id="element-3437" />
                  <XAxis type="number" hide data-id="element-3438" />
                  <YAxis dataKey="name" type="category" width={100} tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} axisLine={false} tickLine={false} data-id="element-3439" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-3440" />
                  <Bar dataKey="time" fill="#06b6d4" radius={[0, 4, 4, 0]} barSize={24} name="Total Time (mins)" data-id="element-3441" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Options and Revenue Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3442">
        <Card data-id="element-3443">
          <CardHeader data-id="element-3444">
            <CardTitle data-id="element-3445">Top Selected Options by Revenue</CardTitle>
          </CardHeader>
          <CardContent data-id="element-3446">
            <div className="space-y-4" data-id="element-3447">
              {optionsData.map((option, index) => <div key={option.name} data-id="element-3448">
                  <div className="flex items-center justify-between mb-2" data-id="element-3449">
                    <div className="flex items-center gap-3" data-id="element-3450">
                      <span className="text-sm font-medium text-slate-700" data-id="element-3451">
                        {index + 1}
                      </span>
                      <span className="text-sm text-slate-900" data-id="element-3452">
                        {option.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-3453">
                      <span className="text-sm font-bold text-emerald-600" data-id="element-3454">
                        ${option.revenue.toLocaleString()}
                      </span>
                      <Badge variant="neutral" data-id="element-3455">{option.percentage}%</Badge>
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-3456">
                    <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{
                  width: `${option.percentage}%`
                }} data-id="element-3457" />
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-3458">
          <CardHeader data-id="element-3459">
            <CardTitle data-id="element-3460">Elevation Preferences</CardTitle>
          </CardHeader>
          <CardContent data-id="element-3461">
            <div className="h-[280px] w-full flex items-center justify-center" data-id="element-3462">
              <ResponsiveContainer width="100%" height="100%" data-id="element-3463">
                <PieChart data-id="element-3464">
                  <Pie data={elevationData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" data-id="element-3465">
                    {elevationData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-3466" />)}
                  </Pie>
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-3467" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4" data-id="element-3468">
              {elevationData.map(item => <div key={item.name} className="flex items-center gap-2" data-id="element-3469">
                  <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: item.color
              }} data-id="element-3470" />
                  <span className="text-sm text-slate-600" data-id="element-3471">{item.name}</span>
                  <span className="text-sm font-medium text-slate-900 ml-auto" data-id="element-3472">
                    {item.value}%
                  </span>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Insights Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3473">
        <Card data-id="element-3474">
          <CardHeader data-id="element-3475">
            <CardTitle data-id="element-3476">Floor Plan Preferences</CardTitle>
          </CardHeader>
          <CardContent data-id="element-3477">
            <div className="h-[240px] w-full" data-id="element-3478">
              <ResponsiveContainer width="100%" height="100%" data-id="element-3479">
                <BarChart data={floorplanData} data-id="element-3480">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-3481" />
                  <XAxis dataKey="name" tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} data-id="element-3482" />
                  <YAxis tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} data-id="element-3483" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-3484" />
                  <Bar dataKey="value" fill="#334155" radius={[4, 4, 0, 0]} barSize={60} data-id="element-3485" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-3486">
          <CardHeader data-id="element-3487">
            <CardTitle data-id="element-3488">Color Palette Selections</CardTitle>
          </CardHeader>
          <CardContent data-id="element-3489">
            <div className="space-y-4" data-id="element-3490">
              {colorPaletteData.map(palette => <div key={palette.name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-3491">
                  <div className="flex items-center gap-3" data-id="element-3492">
                    <div className="w-10 h-10 rounded-md border-2 border-slate-200" style={{
                  backgroundColor: palette.color
                }} data-id="element-3493" />
                    <span className="text-sm font-medium text-slate-900" data-id="element-3494">
                      {palette.name}
                    </span>
                  </div>
                  <span className="text-lg font-bold text-slate-900" data-id="element-3495">
                    {palette.count}
                  </span>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}